import { h, Host } from "@stencil/core";
import { SwipeListener } from "../../service/swipe-listener";
export class Gallery {
    constructor() {
        this.selectedImageIndex = 0;
        this.images = [];
        this.zoom = 1;
        this.positionX = 0;
        this.positionY = 0;
        this.initialPositionX = 0;
        this.initialPositionY = 0;
        this.startX = 0;
        this.startY = 0;
        this.moveX = 0;
        this.moveY = 0;
        this.onMouseMove = (event) => {
            const mouseOrTouchEvent = this.getEvent(event);
            this.moveX = mouseOrTouchEvent.clientX - this.startX;
            this.moveY = mouseOrTouchEvent.clientY - this.startY;
            this.positionX = this.initialPositionX + this.moveX;
            this.positionY = this.initialPositionY + this.moveY;
            if (this.activeImage) {
                this.activeImage.transition = false;
                this.activeImage.translateCoordinates = this.getTransformedPosition(this.positionX, this.positionY, this.zoom);
            }
        };
        this.onMouseUp = () => {
            if (this.activeImage) {
                ['mousemove', 'touchmove']
                    .forEach(eventType => this.activeImage.removeEventListener(eventType, this.onMouseMove));
                ['mouseup', 'touchend']
                    .forEach(eventType => this.activeImage.removeEventListener(eventType, this.onMouseUp));
            }
        };
        this.onMouseDown = (event) => {
            event.preventDefault();
            if (this.zoom > 1 && this.activeImage) {
                const mouseOrTouchEvent = this.getEvent(event);
                this.startX = mouseOrTouchEvent.clientX;
                this.startY = mouseOrTouchEvent.clientY;
                this.initialPositionX = this.positionX;
                this.initialPositionY = this.positionY;
                this.moveX = 0;
                this.moveY = 0;
                ['mousemove', 'touchmove'].forEach(eventType => this.activeImage.addEventListener(eventType, this.onMouseMove));
                ['mouseup', 'touchend'].forEach(eventType => this.activeImage.addEventListener(eventType, this.onMouseUp));
            }
        };
    }
    componentWillLoad() {
        this.images = Array.from(this.el.querySelectorAll('uvex-gallery-image'));
        if (this.selectedImageIndex <= this.images.length - 1) {
            this.selectImage(this.images[this.selectedImageIndex]);
        }
        else if (this.images.length > 0) {
            this.selectImage(this.images[0]);
        }
        this.swipeListener = SwipeListener(this.el);
    }
    disconnectedCallback() {
        this.swipeListener.off();
    }
    onKeyDown(event) {
        if (event.defaultPrevented) {
            return;
        }
        switch (event.key.toLowerCase()) {
            case 'arrowleft':
                this.previousImage();
                break;
            case 'arrowright':
                this.nextImage();
                break;
        }
    }
    onSwipe(e) {
        if (this.zoom !== 1) {
            return;
        }
        const { directions } = e.detail;
        if (directions.left) {
            this.nextImage();
        }
        if (directions.right) {
            this.previousImage();
        }
    }
    getEvent(event) {
        if (event instanceof TouchEvent) {
            const touches = event.touches && event.touches.length ? event.touches : [event];
            return (event.changedTouches && event.changedTouches[0]) || touches[0];
        }
        return event;
    }
    getTransformedPosition(x, y, scale) {
        if (!this.activeImage) {
            return { x: 0, y: 0 };
        }
        var imageRect = this.activeImage.querySelector('img').getBoundingClientRect(), containerRect = this.activeImage.getBoundingClientRect(), minX = Math.max(0, ((imageRect.width - containerRect.width) / scale) / 2), minY = Math.max(0, ((imageRect.height - containerRect.height) / scale) / 2);
        return {
            x: Math.max(minX * -1, Math.min(minX, x)),
            y: Math.max(minY * -1, Math.min(minY, y))
        };
    }
    selectImage(image) {
        const eventTypes = ['mousedown', 'touchstart'];
        this.zoom = 1;
        this.images.forEach(image => {
            eventTypes.forEach(eventType => image.removeEventListener(eventType, this.onMouseDown));
            image.active = false;
            image.zoom = this.zoom;
            delete image.translate;
        });
        this.activeImage = image;
        image.active = true;
        eventTypes.forEach(eventType => image.addEventListener(eventType, this.onMouseDown));
    }
    previousImage() {
        if (this.zoom !== 1) {
            return;
        }
        if (!this.activeImage) {
            if (this.images.length > 0) {
                this.selectImage(this.images[0]);
            }
            return;
        }
        const currentIndex = this.images.indexOf(this.activeImage);
        if (currentIndex <= 0) {
            this.selectImage(this.images[this.images.length - 1]);
        }
        else {
            this.selectImage(this.images[currentIndex - 1]);
        }
    }
    nextImage() {
        if (this.zoom !== 1) {
            return;
        }
        if (!this.activeImage) {
            if (this.images.length > 0) {
                this.selectImage(this.images[0]);
            }
            return;
        }
        const currentIndex = this.images.indexOf(this.activeImage);
        if (currentIndex >= this.images.length - 1) {
            this.selectImage(this.images[0]);
        }
        else {
            this.selectImage(this.images[currentIndex + 1]);
        }
    }
    zoomIn() {
        if (this.zoom < 3) {
            this.zoom += 1;
            this.addZoomToImage(this.zoom);
        }
    }
    zoomOut() {
        if (this.zoom > 1) {
            this.zoom -= 1;
            this.addZoomToImage(this.zoom);
        }
    }
    zoomReset() {
        this.zoom = 1;
        this.addZoomToImage(this.zoom);
    }
    addZoomToImage(zoom) {
        if (this.activeImage) {
            this.activeImage.transition = true;
            this.activeImage.zoom = zoom;
            if (zoom === 1) {
                this.activeImage.translateCoordinates = null;
            }
        }
    }
    render() {
        const thumbnails = this.images.map(image => {
            const props = {
                role: 'button',
                onClick: () => this.selectImage(image),
                class: {
                    'gallery-thumbnail': true,
                    active: this.activeImage === image
                },
            };
            return h("a", Object.assign({}, props), h("img", { src: image.image, class: "gallery-thumbnail-image" }));
        });
        return h(Host, null, h("div", { class: "gallery-container" }, h("slot", null), h("div", { class: "gallery-zoom-control" }, h("a", { class: { 'btn-icon': true, 'disabled': this.zoom === 1 }, onClick: () => this.zoomOut() }, h("svg", { width: "24", height: "24" }, h("use", { href: "#fa-minus" }))), h("a", { class: { 'btn-icon': true, 'disabled': this.zoom === 1 }, onClick: () => this.zoomReset() }, h("svg", { width: "24", height: "24" }, h("use", { href: "#fa-compress" }))), h("a", { class: { 'btn-icon': true, 'disabled': this.zoom === 3 }, onClick: () => this.zoomIn() }, h("svg", { width: "24", height: "24" }, h("use", { href: "#fa-plus" })))), h("a", { role: "button", class: "gallery-control left", onClick: () => this.previousImage() }), h("a", { role: "button", class: "gallery-control right", onClick: () => this.nextImage() })), h("div", { class: "gallery-thumbnails-container" }, h("div", { class: "gallery-thumbnails" }, ...thumbnails)));
    }
    static get is() { return "uvex-gallery"; }
    static get originalStyleUrls() {
        return {
            "$": ["./gallery.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["gallery.css"]
        };
    }
    static get properties() {
        return {
            "selectedImageIndex": {
                "type": "number",
                "attribute": "selected-image-index",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get states() {
        return {
            "activeImage": {},
            "images": {},
            "zoom": {},
            "positionX": {},
            "positionY": {}
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "keydown",
                "method": "onKeyDown",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "swipe",
                "method": "onSwipe",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=gallery.js.map
